import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from 'primereact/toast';
import * as authApi from '../api/auth';
import * as usersApi from '../api/users';

function AuthPage({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const toast = useRef(null);

  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;

  const handleInputChange = (setter) => (e) => {
    setter(e.target.value);
  };

  const validateEmail = () => {
    if (!emailRegex.test(username)) {
      toast.current.show({
        severity: 'error',
        summary: 'Invalid Email',
        detail: 'Please enter a valid email address',
        life: 3000
      });
      return false;
    }
    return true;
  };

  // Google Sign-In initialization
  useEffect(() => {
    window.google?.accounts.id.initialize({
      client_id:
        "728842576259-68rcm61csdn172lmf5d83e1ughdj18l4.apps.googleusercontent.com",
      callback: handleGoogleCallback,
    });
    window.google?.accounts.id.renderButton(
      document.getElementById("googleSignIn"),
      { theme: "outline", size: "large" }
    );
  }, []);

  const handleGoogleCallback = async (response) => {
    const idToken = response.credential;
    try {
      const userData = await authApi.googleLogin(idToken);
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const delay = isMobile ? 500 : 200;
      await new Promise(resolve => setTimeout(resolve, delay));

      try {
        const sessionData = await authApi.me();
        const userEmail = userData.username || userData.email || sessionData.username || sessionData.email;
        if (userEmail) localStorage.setItem("username", userEmail);

        toast.current.show({ severity: 'success', summary: 'Success', detail: 'Google sign-in successful! Redirecting...', life: 3000 });
        if (onLogin) onLogin(userEmail);
        setTimeout(() => navigate("/tax-insights"), isMobile ? 300 : 100);
      } catch {
        toast.current.show({ severity: 'error', summary: 'Session Error', detail: 'Login succeeded but session could not be verified. Please try again.', life: 5000 });
      }
    } catch (error) {
      toast.current.show({ severity: 'error', summary: 'Error', detail: error.response?.data?.message || 'Error during Google sign-in. Please try again.', life: 3000 });
    }
  };

  const handleCreateUser = async () => {
    try {
      const data = await usersApi.createUser(username, password);
      setMessage(data.message || "User created successfully!");
      setMessageType("success");
    } catch (error) {
      setMessage(error.response?.data?.message || "Error creating user.");
      setMessageType("error");
    }
  };

  const handleLogin = async () => {
    if (!validateEmail()) {
      return;
    }

    try {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const userData = await authApi.login(username, password);

      const delay = isMobile ? 500 : 200;
      await new Promise(resolve => setTimeout(resolve, delay));

      try {
        const sessionData = await authApi.me();
        const userEmail = userData.username || userData.email || sessionData.username || sessionData.email || username;
        if (userEmail) localStorage.setItem("username", userEmail);

        toast.current.show({ severity: 'success', summary: 'Success', detail: 'Login successful! Redirecting...', life: 3000 });
        if (onLogin) onLogin(userEmail);
        setTimeout(() => navigate("/tax-insights"), isMobile ? 300 : 100);
      } catch {
        toast.current.show({ severity: 'error', summary: 'Session Error', detail: 'Login succeeded but session could not be verified. Please try again.', life: 5000 });
        setPassword("");
      }
    } catch (error) {
      toast.current.show({ severity: 'error', summary: 'Error', detail: error.response?.data?.message || 'Incorrect username/password. Please try again.', life: 3000 });
      setPassword("");
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div style={styles.container}>
      <Toast ref={toast} position="bottom-center" />
      <div style={styles.formCard}>
      <h2 style={styles.title}>Welcome back</h2>

      <div style={styles.formGroup}>
        <label style={styles.label}>Email</label>
        <input
            type="email"
          placeholder="me@example.com"
          value={username}
          onChange={handleInputChange(setUsername)}
            onBlur={validateEmail}
          style={styles.input}
        />
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Password</label>
        <div style={styles.passwordContainer}>
          <input
            type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
            value={password}
            onChange={handleInputChange(setPassword)}
            style={{ ...styles.input, marginBottom: 0 }}
          />
            <button onClick={togglePasswordVisibility} style={styles.showPasswordButton}>
            {showPassword ? "Hide" : "Show"}
          </button>
        </div>
        <div style={styles.forgotPassword}>
            <a href="/reset-password" style={styles.forgotPasswordLink}>Forgot password?</a>
        </div>
      </div>

      <button onClick={handleLogin} style={styles.button}>
        Log in
      </button>

        <div style={styles.divider}>
          <div style={styles.dividerLine}></div>
          <span style={styles.dividerText}>or continue with</span>
          <div style={styles.dividerLine}></div>
        </div>

        <div id="googleSignIn" style={styles.googleButton}></div>

      <div style={styles.signUpText}>
        Not a client yet?{" "}
        <a href="/signup" style={styles.signUpLink}>
          Sign up
        </a>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: 'calc(100vh - 80px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    backgroundColor: '#f8f9fa',
  },
  formCard: {
    width: '100%',
    maxWidth: '400px',
    padding: '32px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
    margin: '20px auto',
  },
  title: {
    fontSize: '28px',
    color: '#264653',
    marginBottom: '30px',
    fontWeight: '600',
    textAlign: 'center',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '15px',
    color: '#264653',
    marginBottom: '8px',
    fontWeight: '500',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '15px',
    borderRadius: '8px',
    border: '2px solid #e0e0e0',
    transition: 'all 0.2s ease',
    '&:focus': {
      borderColor: '#2a9d8f',
      outline: 'none',
      boxShadow: '0 0 0 3px rgba(42, 157, 143, 0.1)',
    },
  },
  passwordContainer: {
    position: 'relative',
  },
  showPasswordButton: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    color: '#2a9d8f',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    padding: '4px 8px',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(42, 157, 143, 0.1)',
    },
  },
  forgotPassword: {
    textAlign: 'right',
    marginTop: '8px',
  },
  forgotPasswordLink: {
    fontSize: '14px',
    color: '#2a9d8f',
    textDecoration: 'none',
    fontWeight: '500',
    transition: 'color 0.2s ease',
    '&:hover': {
      color: '#1f7268',
    },
  },
  button: {
    width: '100%',
    padding: '14px',
    fontSize: '16px',
    backgroundColor: '#264653',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'all 0.2s ease',
    marginTop: '10px',
    '&:hover': {
      backgroundColor: '#1a323d',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: '24px 0',
    gap: '15px',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: '#e0e0e0',
  },
  dividerText: {
    color: '#666',
    fontSize: '14px',
    whiteSpace: 'nowrap',
  },
  googleButton: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '20px',
  },
  signUpText: {
    fontSize: '15px',
    color: '#264653',
    textAlign: 'center',
  },
  signUpLink: {
    color: '#2a9d8f',
    textDecoration: 'none',
    fontWeight: '600',
    transition: 'color 0.2s ease',
    '&:hover': {
      color: '#1f7268',
    },
  },
};

export default AuthPage;
